import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Theme, Typography, Button } from '@material-ui/core';
import { CheckCircle, Error } from '@material-ui/icons';
import { useQueryParam } from 'use-query-params';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useApiUrl } from '../../global/config/useApiUrl';
import { useCampfireFetch } from '../../global/network/useCampfireFetch';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 8,
      backgroundColor: 'white',
      padding: '48px 36px',
      width: 905,
      border: 'none',
      boxShadow: 'rgba(35, 63, 77, 0.1) 0px 8px 24px 8px',
      paddingTop: 120,
      paddingBottom: 100,
      color: theme.color.grey.neutral400,
    },
    body: {
      width: 326,
      textAlign: 'center',
      marginTop: 36,
    },
    successIcon: {
      fontSize: 64,
      color: '#4CAF50',
      marginBottom: 16,
    },
    errorIcon: {
      fontSize: 64,
      color: '#f44336',
      marginBottom: 16,
    },
    button: {
      marginTop: 24,
      backgroundColor: '#FF6B35',
      color: 'white',
      '&:hover': {
        backgroundColor: '#E55A2B',
      },
    },
  })
);

export function SubscribedSuccessScreen() {
  const classes = useStyles();
  const [sessionId] = useQueryParam('session_id');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const apiUrl = useApiUrl();
  const campfireCheckout = useCampfireFetch<{ url: string }>({ defer: true });

  useEffect(() => {
    if (apiUrl && sessionId) {
      campfireCheckout
        .run({
          url: `${apiUrl}/tm/subscriptions/save`,
          method: 'post',
          data: {
            sessionId: sessionId,
          },
        })
        .then(() => {
          setStatus('success');
          // Redirect to main app after 3 seconds
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        })
        .catch(() => {
          setStatus('error');
        });
    }
  }, [sessionId, apiUrl]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <CircularProgress />
            <Typography className={classes.body}>
              Hang tight! We&apos;re updating your organisation&apos;s settings now
            </Typography>
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircle className={classes.successIcon} />
            <Typography variant='h5' style={{ marginBottom: 16, fontWeight: 600 }}>
              Welcome to Volaby!
            </Typography>
            <Typography className={classes.body}>
              Your subscription has been activated successfully. You&apos;ll be redirected to your dashboard in a
              moment.
            </Typography>
            <Button
              variant='contained'
              className={classes.button}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Go to Dashboard
            </Button>
          </>
        );
      case 'error':
        return (
          <>
            <Error className={classes.errorIcon} />
            <Typography variant='h5' style={{ marginBottom: 16, fontWeight: 600 }}>
              Something went wrong
            </Typography>
            <Typography className={classes.body}>
              We couldn&apos;t activate your subscription. Please contact support if this continues.
            </Typography>
            <Button
              variant='contained'
              className={classes.button}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Go to Dashboard
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' className={classes.root}>
      {renderContent()}
    </Box>
  );
}
